/* eslint-disable react/jsx-no-bind -- Controller's render prop + per-field handlers cannot be
   hoisted to useCallback (they close over the render-prop's `field`); same precedent as the
   other RHF wrappers. */
import React from 'react'
import { Controller } from 'react-hook-form'

import HHTextField from './HHTextField'

import type { SxProps, Theme } from '@mui/material/styles'
import type { Control, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'

// Untyped .js child: the one boundary cast lives here instead of at every call site.
const HHTextFieldUntyped = HHTextField as React.ElementType

export type HHFormTextFieldProps<TField extends FieldValues = FieldValues> = {
  name: FieldPath<TField>
  control: Control<TField>
  sx?: SxProps<Theme>
  disabled?: boolean
  readOnly?: boolean
  label?: React.ReactNode
  placeholder?: string
  size?: 'small' | 'medium'
  autocomplete?: string
  variant?: 'filled' | 'outlined' | 'standard'
  rules?: RegisterOptions<TField>
  required?: boolean
  fullWidth?: boolean
  InputProps?: Record<string, unknown>
  endLabelIcon?: React.ReactNode
  trimLeft?: boolean
  regexValidators?: RegExp[]
  onKeyPress?: (event: React.KeyboardEvent) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  error?: string
  onChange?: (value: unknown) => void
  onBlur?: (event: React.FocusEvent) => void
  // false = label inside the field (new style); true = legacy label above the field.
  deprecatedLabel?: boolean
  helperText?: string
  type?: React.InputHTMLAttributes<HTMLInputElement>['type']
  multiline?: boolean
  minRows?: number
  maxRows?: number
  defaultValue?: string
}

// A keystroke failing any `regexValidators` pattern never reaches form state.
const HHFormTextField = <TField extends FieldValues = FieldValues>({
  sx = undefined,
  name,
  control,
  disabled = false,
  label = undefined,
  placeholder = '',
  size = 'medium',
  autocomplete = 'off',
  variant = 'outlined',
  rules = undefined,
  required = false,
  fullWidth = false,
  InputProps = undefined,
  endLabelIcon = undefined,
  trimLeft = false,
  regexValidators = [],
  onKeyPress,
  onChange,
  onBlur,
  deprecatedLabel = true,
  ...rest
}: HHFormTextFieldProps<TField>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { ref, onChange: onChangeHookForm, onBlur: onBlurHookForm, ...fieldRest } = field
        return (
          <HHTextFieldUntyped
            autoComplete={autocomplete}
            deprecatedLabel={deprecatedLabel}
            disabled={disabled}
            endIcon={endLabelIcon}
            error={fieldState.error?.message}
            InputProps={{ ...InputProps }}
            inputRef={ref}
            label={required && !deprecatedLabel ? `${label}*` : label}
            placeholder={placeholder}
            required={required}
            size={size}
            sx={sx}
            variant={variant}
            onBlur={(event: React.FocusEvent) => {
              onBlurHookForm()
              onBlur?.(event)
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              let nextValue: unknown = event.target.value
              if (typeof nextValue === 'string') {
                if (trimLeft) nextValue = nextValue.trimStart()
                if (regexValidators.length > 0 && !regexValidators.every(regex => regex.test(String(nextValue)))) return
              }
              // RHF accepts a raw value — avoids mutating the event to apply the transforms.
              onChangeHookForm(nextValue)
              onChange?.(nextValue)
            }}
            onKeyPress={onKeyPress}
            {...fieldRest}
            {...rest}
            fullWidth={fullWidth}
          />
        )
      }}
      rules={rules}
    />
  )
}

export default HHFormTextField
